import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import Logo from './Logo';
import Button from './Button';
import { theme } from '../../styles/GlobalStyles';

const HeaderContainer = styled.header`
  position: fixed;
  width: 100%;
  z-index: 1000;
  transition: all 0.3s ease;
  top: 0;

  ${props => props.scrolled ? css`
    background: rgba(7,8,15,0.85);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(255,255,255,0.07);
    box-shadow: 0 4px 24px rgba(0,0,0,0.4);
  ` : css`
    background: transparent;
  `}
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
  max-width: 1200px;
  margin: 0 auto;
  height: ${props => props.scrolled ? '64px' : '72px'};
  transition: height 0.3s ease;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  @media (max-width: 900px) { display: none; }
`;

const NavItem = styled.div`
  padding: 0.45rem 0.8rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.textMuted};
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.text};
    background: rgba(255,255,255,0.05);
  }
  &.active {
    color: ${theme.text};
    background: rgba(97,93,255,0.1);
    border-color: rgba(97,93,255,0.2);
  }
  a { color: inherit; text-decoration: none; }
`;

const ButtonContainer = styled.div`
  margin-left: 0.75rem;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: rgba(255,255,255,0.05);
  border: 1px solid ${theme.border};
  font-size: 1.2rem;
  cursor: pointer;
  color: ${theme.textMuted};
  padding: 0.5rem 0.6rem;
  border-radius: 8px;
  transition: all 0.2s;
  &:hover { color: ${theme.text}; background: rgba(97,93,255,0.1); }
  @media (max-width: 900px) { display: flex; align-items: center; }
`;

const MobileMenu = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(7,8,15,0.97);
  backdrop-filter: blur(24px);
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem; right: 2rem;
  font-size: 1.5rem;
  cursor: pointer;
  background: rgba(255,255,255,0.05);
  border: 1px solid ${theme.border};
  color: ${theme.textMuted};
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  &:hover { color: ${theme.text}; }
`;

const MobileNavItem = styled.div`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${theme.textMuted};
  margin: 0.4rem 0;
  a { color: inherit; text-decoration: none; transition: color 0.2s; }
  a:hover { color: ${theme.text}; }
`;

const MobileButtonContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
`;

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/prodseal/demo';

const navLinks = [
  { to: '/',             label: 'Home' },
  { to: '/about',        label: 'About' },
  { to: '/solutions',    label: 'Solutions' },
  { to: '/industries',   label: 'Industries' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/resources',    label: 'Resources' },
  { to: '/contact',      label: 'Contact' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const location                     = useLocation();

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    const linkEl   = document.createElement('link');
    linkEl.rel     = 'stylesheet';
    linkEl.href    = 'https://assets.calendly.com/assets/external/widget.css';
    const scriptEl = document.createElement('script');
    scriptEl.src   = 'https://assets.calendly.com/assets/external/widget.js';
    scriptEl.async = true;
    document.head.appendChild(linkEl);
    document.head.appendChild(scriptEl);
    return () => {
      document.head.removeChild(linkEl);
      document.head.removeChild(scriptEl);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const isActive = (to) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <HeaderContainer scrolled={scrolled}>
      <HeaderContent scrolled={scrolled}>
        <Logo />
        <Nav>
          {navLinks.map(({ to, label }) => (
            <NavItem key={to} className={isActive(to) ? 'active' : ''}>
              <Link to={to}>{label}</Link>
            </NavItem>
          ))}
          <ButtonContainer>
            <Button primary onClick={openCalendly}>Book a Demo</Button>
          </ButtonContainer>
        </Nav>
        <MobileMenuButton onClick={() => setIsMenuOpen(true)} aria-label="Open menu">
          <FaBars />
        </MobileMenuButton>
      </HeaderContent>

      <MobileMenu isOpen={isMenuOpen}>
        <CloseButton onClick={() => setIsMenuOpen(false)} aria-label="Close menu">
          <FaTimes />
        </CloseButton>
        {navLinks.map(({ to, label }) => (
          <MobileNavItem key={to}>
            <Link to={to}>{label}</Link>
          </MobileNavItem>
        ))}
        <MobileButtonContainer>
          <Button secondary to="/contact">Contact Us</Button>
          <Button primary onClick={() => { openCalendly(); setIsMenuOpen(false); }}>
            Book a Demo
          </Button>
        </MobileButtonContainer>
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header;
